import { z } from 'zod';

import { textOverridesApiUrl } from '@/domain/textOverrides/config';
import {
  TextOverridesBundleSchema,
  TextOverridesErrorSchema,
  TextOverridesVersionSchema,
  TextOverrideHistoryResponseSchema,
  TextOverrideSaveBodySchema,
  TextOverrideWriteResponseSchema,
} from '@/domain/textOverrides/schemas';

/*
 * Plain fetch, not the app's axios instances: their response interceptors sign the user out on some status codes,
 * and a proof-of-concept service must never be able to do that. No auth — the POC service has none.
 */

export class TextOverridesApiError extends Error {
  readonly reasons: ReadonlyArray<string>;

  constructor(message: string, reasons: ReadonlyArray<string> = []) {
    super(message);
    this.name = 'TextOverridesApiError';
    this.reasons = reasons;
  }
}

const toReasons = (details: unknown): ReadonlyArray<string> =>
  Array.isArray(details) ? details.map(String) : details === undefined ? [] : [String(details)];

const request = async <S extends z.ZodTypeAny>(path: string, schema: S, init?: RequestInit): Promise<z.output<S>> => {
  const response = await fetch(`${textOverridesApiUrl}${path}`, {
    ...init,
    headers: { accept: 'application/json', ...(init?.body ? { 'content-type': 'application/json' } : {}) },
  }).catch((e: unknown) => {
    throw new TextOverridesApiError(`Text overrides service unreachable: ${e instanceof Error ? e.message : String(e)}`);
  });
  const body: unknown = await response.json().catch(() => undefined);
  if (!response.ok) {
    const parsed = TextOverridesErrorSchema.safeParse(body);
    throw parsed.success
      ? new TextOverridesApiError(parsed.data.error, toReasons(parsed.data.details))
      : new TextOverridesApiError(`Text overrides service answered ${response.status}`);
  }
  return schema.parse(body);
};

const orgPath = (orgId: string) => `/orgs/${encodeURIComponent(orgId)}`;
const keyPath = (orgId: string, key: string) => `${orgPath(orgId)}/overrides/${encodeURIComponent(key)}`;
const localeQuery = (locale: string) => `?${new URLSearchParams({ locale })}`;

export const getTextOverridesBundle = (orgId: string, locale: string) =>
  request(`${orgPath(orgId)}/bundle${localeQuery(locale)}`, TextOverridesBundleSchema);

export const saveTextOverride = (orgId: string, key: string, body: z.input<typeof TextOverrideSaveBodySchema>) =>
  request(keyPath(orgId, key), TextOverrideWriteResponseSchema, {
    method: 'PUT',
    body: JSON.stringify(TextOverrideSaveBodySchema.parse(body)),
  });

export const resetTextOverride = (orgId: string, key: string, locale: string) =>
  request(`${keyPath(orgId, key)}${localeQuery(locale)}`, TextOverrideWriteResponseSchema, { method: 'DELETE' });

export const getTextOverrideHistory = (orgId: string, key: string, locale: string) =>
  request(`${keyPath(orgId, key)}/history${localeQuery(locale)}`, TextOverrideHistoryResponseSchema);

export const getTextOverridesVersion = (orgId: string) => request(`${orgPath(orgId)}/version`, TextOverridesVersionSchema);
